import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Spacer } from "./Spacer";
import { TextCapitalized } from "./text/TextCapitalized";
import { ModalDS } from "./Modal";
import { FileInputButton } from "../../components/input/FileInputButton";
import { triggerToastDS } from "./TriggerToast";
import { colors } from "../../styles/figmaColors";
import { typographies } from "../../styles/figmaTypographies";
import { AddOutlined, DeleteOutlined } from "../materialUi/materialUi";

const photoExtensionRegex = /\.(jpe?g|png)$/i;

export interface PhotosInputDSProps {
  photos: File[];
  setPhotos?: (files: File[]) => void;
  canAdd?: boolean;
  canRemove?: boolean;
  max?: number;
}

export const PhotosInputDS = ({ max = 6, ...props }: PhotosInputDSProps) => {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<string[]>([]);
  const [zoomedIndex, setZoomedIndex] = useState<number | null>(null);

  useEffect(() => {
    const created = props.photos.map((file) => URL.createObjectURL(file));
    setUrls(created);
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [props.photos]);

  const add = (file: File | null) => {
    if (!file || !props.setPhotos) return;
    if (!photoExtensionRegex.test(file.name)) {
      triggerToastDS(t("photosInput.error.type") || "", "ERROR");
      return;
    }
    props.setPhotos([...props.photos, file]);
  };

  const remove = (index: number) => {
    if (props.setPhotos)
      props.setPhotos(props.photos.filter((_, i) => i !== index));
  };

  const showAdd =
    !!props.canAdd && !!props.setPhotos && props.photos.length < max;

  return (
    <StyledPhotos>
      {urls.map((url, index) => (
        <StyledThumbnail key={url}>
          <StyledImg src={url} alt="" onClick={() => setZoomedIndex(index)} />
          {props.canRemove && props.setPhotos && (
            <StyledDelete onClick={() => remove(index)}>
              <DeleteOutlined />
            </StyledDelete>
          )}
        </StyledThumbnail>
      ))}
      {showAdd && (
        <FileInputButton handleFile={add}>
          <StyledAddTile>
            <StyledAddIcon>
              <AddOutlined />
            </StyledAddIcon>
            <Spacer y={0.5} />
            <TextCapitalized>{t("photosInput.add")}</TextCapitalized>
          </StyledAddTile>
        </FileInputButton>
      )}
      <ModalDS
        visible={zoomedIndex !== null}
        onClose={() => setZoomedIndex(null)}
      >
        {zoomedIndex !== null ? (
          <StyledZoom>
            <Spacer y={1} />
            <StyledZoomImg src={urls[zoomedIndex]} alt="" />
          </StyledZoom>
        ) : (
          <></>
        )}
      </ModalDS>
    </StyledPhotos>
  );
};

const StyledPhotos = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const StyledThumbnail = styled.div`
  position: relative;
  aspect-ratio: 1;
`;

const StyledImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.5rem;
`;

const StyledDelete = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: ${colors["colors/text/white"]};
  & svg {
    width: 1rem;
    height: 1rem;
    fill: ${colors["colors/system/error/error_normal"]};
  }
`;

const StyledAddTile = styled.div`
  ${typographies["Body/XS"]}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  box-sizing: border-box;
  text-align: center;
  color: ${colors["colors/text/lightGrey"]};
  background-color: ${colors["colors/text/white"]};
  border: 0.0625rem dashed ${colors["colors/borders/cards/cards"]};
  border-radius: 0.5rem;
`;

const StyledAddIcon = styled.div`
  display: flex;
  border-radius: 50%;
  border: 0.0625rem solid ${colors["colors/text/black"]};
  & svg {
    width: 1rem;
    height: 1rem;
    fill: ${colors["colors/text/black"]};
  }
`;

const StyledZoom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledZoomImg = styled.img`
  max-width: 100%;
  max-height: 70vh;
`;
